import { Link, Navigate, useParams } from "react-router";

import { RegistryFailure, RegistryLoading } from "@/components/RegistryLookupState";
import { RegistryRecordCard } from "@/components/RegistryRecordCard";
import { Alert, Card, PageHeader } from "@/components/ui";
import { NETWORK_NAME } from "@/lib/config";
import { usePageMeta } from "@/lib/page-meta";
import { resolveRegistryId } from "@/lib/registry/ids";
import { useRegistryLookup } from "@/lib/registry/use-lookup";

import { NotFoundPage } from "./NotFoundPage";

const CONTEXT = "Vendor terverifikasi";

export function VendorPage() {
  const { registryId: raw = "" } = useParams();

  usePageMeta({
    title: `Vendor ${decodeURIComponent(raw)}`,
    description: "Status vendor resmi ASETQU, dibaca dari UtilityRegistry di rantai.",
  });

  const resolved = resolveRegistryId(raw);
  // Vendors are always version 1 — there is no second generation of "this
  // company is verified", and the registry rejects any other version.
  const lookup = useRegistryLookup(resolved ?? undefined, 1);

  if (!resolved) return <NotFoundPage />;
  if (lookup.status === "loading") return <RegistryLoading context={CONTEXT} />;

  if (!lookup.lookup.ok) {
    // An unconfigured registry is not this page's story to tell: /verify says
    // "we cannot check" in words, and sending people there keeps one wording
    // for that answer instead of three.
    if (lookup.lookup.failure.kind === "unconfigured") {
      return <Navigate to={`/verify/${raw}`} replace />;
    }
    return <RegistryFailure context={CONTEXT} failure={lookup.lookup.failure} />;
  }

  const record = lookup.lookup.record;

  // A link that guessed wrong about the record type should still land somewhere
  // correct rather than render a vendor page for a utility.
  if (record.registryType !== "ExternalVendor" && record.status !== "Unregistered") {
    return <Navigate to={`/utility/${raw}`} replace />;
  }

  const children = record.children;

  return (
    <div className="page-shell">
      <PageHeader
        context={CONTEXT}
        title={record.name ?? "Vendor"}
        description={`Status resmi dibaca dari UtilityRegistry di ${NETWORK_NAME}.`}
      />

      <RegistryRecordCard record={record} />

      <Card title="Layanan berbasis ASETQU">
        {children.length === 0 ? (
          <p className="text-sm text-ink-muted">
            Vendor ini belum mendaftarkan layanan apa pun yang bertransaksi dengan ASETQU. Itu
            normal — banyak vendor hanya membutuhkan identitas dan sertifikat.
          </p>
        ) : (
          <>
            <p className="mb-3 text-sm text-ink-muted">
              Layanan di bawah ini berhenti menerima aktivitas baru secara otomatis kalau vendor
              ditangguhkan atau dicabut — tanpa transaksi apa pun terhadap catatan layanannya.
            </p>
            <ul className="flex flex-col gap-2">
              {children.map((child) => (
                <li key={child}>
                  <Link
                    className="font-mono text-sm text-brand underline decoration-dotted underline-offset-4 hover:decoration-solid"
                    to={`/utility/${child}`}
                  >
                    {child}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      <Alert tone="info">
        Sertifikat NFT bukan bukti status. Sertifikat tetap berada di wallet vendor walaupun
        statusnya sudah ditangguhkan atau dicabut — yang berlaku adalah status di halaman ini,
        yang dibaca langsung dari registry.
      </Alert>
    </div>
  );
}
